import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import Cropper from 'react-cropper'
import 'cropperjs/dist/cropper.css'
import { CloudUploadIcon } from '@heroicons/react/solid'

import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import { PrimaryButton, DefaultButton } from '@/shared/Buttons'

const dataURItoBlob = (dataURI) => {
  const binary = atob(dataURI.split(',')[1])
  const array = []
  for (let i = 0; i < binary.length; i++) array.push(binary.charCodeAt(i))
  return new Blob([new Uint8Array(array)], { type: 'image/jpeg' })
}

const ImageUploadFormR2 = (props) => {
  const { kind = 'User', url, setUrl, aspect = 'square', onDone } = props

  const aspectRatio = aspect === 'video' ? 16 / 9 : 1 / 1
  const cropStyle = aspect === 'video' ? { minHeight: 450, minWidth: '100%' } : { minHeight: 400, minWidth: 400 }
  const coverBox = aspect === 'video' ? 'h-54 w-96 sm:w-960px sm:h-540px sm:w-960' : 'h-96 w-96'

  const [, setToast] = useGlobalState('toast')
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()

  const cropperRef = useRef(null)
  const isComponentMounted = useRef(false)

  const [editing, setEditing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [thisPreview, setThisPreview] = useState(url)
  const [file, setFile] = useState(null)

  useEffect(() => {
    isComponentMounted.current = true
    return () => { isComponentMounted.current = false }
  }, [])

  useEffect(() => {
    if (!file) return
    setThisPreview(URL.createObjectURL(file))
  }, [file])

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-red-600'>Error</p>
        <p className='mt-1 text-sm text-red-400'>You can only upload jpg, png, gif, or webp files. File can't be over 8MB.</p>
      </div>)
    }
    if (acceptedFiles.length > 0) setFile(acceptedFiles[0])
  }, [])

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: 'image/*', maxFiles: 1, maxSize: 8388608 })

  const upload = (action) => {
    if (!file) return
    setUploading(true)

    const contentType = action === 'edited' ? 'image/jpeg' : file.type
    const extension = action === 'edited' ? 'jpg' : file.name.split('.').pop().toLowerCase()
    const baseName = file.name.replace(/\.[^/.]+$/, '').replace(/[^\w\d_\-.]+/ig, '').slice(0, 40) || 'image'
    const rand = Math.random().toString(36).slice(2, 8)
    const key = `${kind.toLowerCase()}-${currentUser.id}-${Date.now()}-${rand}-${baseName}.${extension}`

    getRequest('/api/v1/image_uploads/r2_hash', { key, kind, content_type: contentType }, (err, jsonData) => {
      if (err || !jsonData?.url) {
        setUploading(false)
        setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
          <p className='text-sm font-medium text-red-600'>Upload error</p>
          <p className='mt-1 text-sm text-red-400'>{err?.errors || err || 'Could not get an upload URL.'}</p>
        </div>)
        return
      }

      let body = file
      if (action === 'edited') {
        const dataUrl = cropperRef.current.cropper.getCroppedCanvas().toDataURL('image/jpeg', 0.92)
        body = new File([dataURItoBlob(dataUrl)], key, { type: 'image/jpeg' })
      }

      window.fetch(jsonData.url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body
      }).then((response) => {
        if (!response.ok) {
          setUploading(false)
          setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
            <p className='text-sm font-medium text-red-600'>Upload error</p>
            <p className='mt-1 text-sm text-red-400'>R2 returned {response.status}.</p>
          </div>)
          return
        }
        const finalUrl = jsonData.image_url + key
        if (isComponentMounted.current) {
          setUploading(false)
          setEditing(false)
          setFile(null)
          setThisPreview(finalUrl)
        }
        setUrl(finalUrl)
        if (typeof onDone === 'function') onDone(finalUrl)
      }).catch(() => {
        setUploading(false)
        setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
          <p className='text-sm font-medium text-red-600'>Upload error</p>
          <p className='mt-1 text-sm text-red-400'>Network error talking to R2.</p>
        </div>)
      })
    })
  }

  if (!editing) {
    return <div>
      <div className={`${coverBox} relative block border-2 border-gray-300 flex flex-col justify-center cursor-pointer border-dashed rounded-lg p-12 text-center hover:border-gray-400`} {...getRootProps()}>
        <input {...getInputProps()} />

        {thisPreview && aspect === 'square' && <div className='overflow-hidden aspect-w-1 aspect-h-1 w-full'>
          <img className='object-cover w-full' src={thisPreview} alt='' />
        </div>}

        {thisPreview && aspect === 'video' && <div className='overflow-hidden aspect-w-16 aspect-h-9 w-full'>
          <img className='object-cover w-full' src={thisPreview} alt='' />
        </div>}

        {!thisPreview && <>
          <CloudUploadIcon className='mx-auto h-12 w-12 text-gray-400' />
          <span className='mt-2 block text-sm font-medium text-gray-900 dark:text-gray-300'>Click or drop a file to upload</span>
        </>}
      </div>

      {aspect === 'square' && <div className='p-4 text-xs text-gray-500 dark:text-gray-400'>Square image. For best results, at least 500×500.</div>}
      {aspect === 'video' && <div className='p-4 text-xs text-gray-500 dark:text-gray-400'>16:9 image. For best results, at least 800×450.</div>}
      {file && <div className='text-xs text-gray-500 dark:text-gray-400'>{file.path} - {file.size} bytes</div>}

      {file && <div className='flex justify-end mt-5'>
        <PrimaryButton loading={uploading} onClick={() => upload('original')} text='Save' />
        <DefaultButton className='ml-5' onClick={() => setEditing(true)} text='Crop & Save' />
      </div>}
    </div>
  }

  return (
    <div>
      <div className='flex justify-center flex-col'>
        <Cropper
          ref={cropperRef}
          src={thisPreview}
          style={cropStyle}
          movable={false}
          zoomable={false}
          rotatable={false}
          scalable={false}
          initialAspectRatio={aspectRatio}
          aspectRatio={aspectRatio}
          guides
        />
      </div>
      <div className='flex justify-end mt-5'>
        <PrimaryButton loading={uploading} onClick={() => upload('edited')} text='Save Crop' />
        <DefaultButton className='ml-5' onClick={() => setEditing(false)} text='Cancel' />
      </div>
    </div>
  )
}

export default ImageUploadFormR2
